import {
  Typography,
  ListItemText,
  ListItem,
  makeStyles,
  Chip,
  Grid,
  ListItemSecondaryAction,
  ListItemIcon,
  Checkbox,
} from "@material-ui/core";
import { useFieldLookup } from "@ofqual/portal-core/core/hooks";
import React, { useState } from "react";
import {
  cachedApprenticeshipStandardService,
  cachedLevelsService,
  cachedPermissionTypesService,
  cachedQualificationTypeService,
  cachedSectorSubjectAreaService,
  cachedTechnicalQualSubjectService,
} from "../../services";
import { ApplicationScopeStatus, Scope } from "../../domain/Scope";
import { ScopeDialog } from "./ScopeDialog";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";
import {
  formatUtcDate,
  isFutureDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { endOfDay } from "date-fns";

const useStyles = makeStyles((theme) => ({
  submissionRowDetails: {
    paddingRight: "20px",
  },
}));

export type ScopeListItemProps = {
  item: Scope;
  scopes: Scope[];
  canEdit?: boolean;
  toggleDeleteScope: (scope: Scope) => void;
  canApprove?: boolean;
  onUpdate?: (scope: Scope, lookupTrackingInfo: LookupTrackerStore) => void;
  existingScopes?: Scope[];
};

export const ScopeListItem: React.FC<ScopeListItemProps> = ({
  item,
  scopes,
  canEdit,
  toggleDeleteScope,
  canApprove,
  onUpdate,
  existingScopes,
}) => {
  const classes = useStyles();

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const qualType = useFieldLookup(
    {
      service: cachedQualificationTypeService,
      label: "description",
    },
    item.qualificationType
  );

  const ssa = useFieldLookup(
    cachedSectorSubjectAreaService,
    item.sectorSubjectArea
  );

  const permType = useFieldLookup(
    {
      service: cachedPermissionTypesService,
      label: "description",
    },
    item.recognitionPermissionType
  );

  const level = useFieldLookup(
    {
      service: cachedLevelsService,
      label: "levelValue",
    },
    item.level
  );

  const technicalQual = useFieldLookup(
    {
      service: cachedTechnicalQualSubjectService,
      label: "description",
    },
    item.tLevelSubject
  );

  const apprenticeshipStandard = useFieldLookup(
    {
      service: cachedApprenticeshipStandardService,
      label: "title",
    },
    item.apprenticeshipStandard
  );

  return (
    <>
      <ListItem
        alignItems="flex-start"
        divider
        data-test="row"
        button={canApprove as any}
        onClick={() => canApprove && setOpenDialog(true)}
      >
        {canEdit ? (
          <ListItemIcon>
            <Checkbox
              checked={item.shouldDelete || false}
              name={`clear-checkbox`}
              onChange={() => toggleDeleteScope(item)}
            />
          </ListItemIcon>
        ) : null}
        <Grid container component="span">
          <Grid item component="span" xs={12}>
            <ListItemText
              primary={
                <Typography component="span" variant="h6" color="textPrimary">
                  {item.qualificationDescription
                    ? item.qualificationDescription
                    : item.qualificationType
                    ? qualType
                    : item.sectorSubjectArea
                    ? ssa
                    : ""}
                </Typography>
              }
              secondary={
                <Grid container spacing={3} component="span">
                  <Grid item component="span" md={11} xs={12}>
                    <Typography
                      className={classes.submissionRowDetails}
                      component="span"
                      variant="body2"
                      color="textPrimary"
                    >
                      <strong>Permission Type: </strong>
                      {permType}
                    </Typography>
                    {item.apprenticeshipStandard ? (
                      <Typography
                        className={classes.submissionRowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>Apprenticeship Standard: </strong>
                        {apprenticeshipStandard}
                      </Typography>
                    ) : null}
                    {item.tLevelSubject ? (
                      <Typography
                        className={classes.submissionRowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>Technical Qualification Subject: </strong>
                        {technicalQual}
                      </Typography>
                    ) : null}
                    {item.sectorSubjectArea ? (
                      <Typography
                        className={classes.submissionRowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>Sector Subject Area: </strong>
                        {ssa}
                      </Typography>
                    ) : null}
                    {item.level ? (
                      <>
                        <br />
                        <Typography
                          className={classes.submissionRowDetails}
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          <strong>Level: </strong>
                          {level}
                        </Typography>
                      </>
                    ) : null}
                    {item.status ? (
                      <Typography
                        className={classes.submissionRowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>Status: </strong>
                        {ApplicationScopeStatus[item.status]}
                      </Typography>
                    ) : null}
                    {/* {item.validStartDate ? (
                      <Typography
                        className={classes.submissionRowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>Valid start: </strong>
                        {(new Date(item.validStartDate) < endOfDay(new Date()) && item.status) 
                          ? "On recognition"
                          : formatUtcDate(item.validStartDate,standardDateFormats.DATE)
                        }
                      </Typography>
                    ) : null} */}
                    {item.active !== undefined ? (
                      <>
                        <Typography
                          className={classes.submissionRowDetails}
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          <strong>Status: </strong>
                          {item.active ? "Active" : "Inactive"}
                        </Typography>
                      </>
                    ) : null}
                    {item.validStartDate && isFutureDate(item.validStartDate) ? (
                      <>
                        <Typography
                          className={classes.submissionRowDetails}
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          <strong>Valid start: </strong>
                          {formatUtcDate(
                            item.validStartDate,
                            standardDateFormats.DATE
                          )}
                        </Typography>
                      </>
                    ) : null}
                  </Grid>
                </Grid>
              }
            />
            <ListItemSecondaryAction>
              <Chip
                size="small"
                label={item.inclusionExclusion === 0 ? "Excluded" : "Included"}
                color="primary"
                component="span"
              />
            </ListItemSecondaryAction>
          </Grid>
        </Grid>
      </ListItem>
      {openDialog && item ? (
        <ScopeDialog
          onClose={() => setOpenDialog(false)}
          scopes={scopes}
          onUpdate={onUpdate}
          scopeToUpdate={item}
          canApprove={canApprove}
          existingScopes={existingScopes}
        />
      ) : null}
    </>
  );
};
